<template>
  <div class="orden-ver" style="padding:1.25rem;color:var(--text-primary)">

    <!-- Cabecera -->
    <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
      <h5 class="mb-0 fw-semibold" style="color:var(--text-primary)">
        <i class="bi bi-tools me-2" style="color:var(--primary-main)"></i>
        Orden {{ orden ? orden.numero_orden : '...' }}
      </h5>
      <div class="d-flex gap-2 flex-wrap">
        <button
          v-if="puedeEditar && orden && (orden.estado === 'abierta' || orden.estado === 'en_proceso')"
          class="btn btn-sm btn-outline-secondary"
          @click="$router.push('/taller/ordenes/' + orden.id + '/editar')"
        >
          <i class="bi bi-pencil me-1"></i>Editar
        </button>
        <button
          v-if="puedeEditar && orden && orden.estado !== 'cancelada' && orden.estado !== 'facturada'"
          class="btn btn-sm btn-outline-primary"
          @click="abrirModalEstado"
        >
          <i class="bi bi-arrow-left-right me-1"></i>Cambiar estado
        </button>
        <button
          v-if="puedeEditar && orden && orden.estado !== 'cancelada' && orden.estado !== 'facturada'"
          class="btn btn-sm btn-success"
          @click="abrirModalConversion"
        >
          <i class="bi bi-receipt-cutoff me-1"></i>Convertir a Factura
        </button>
        <button class="btn btn-sm btn-outline-secondary" @click="$router.push('/taller/ordenes')">
          <i class="bi bi-arrow-left me-1"></i>Volver
        </button>
      </div>
    </div>

    <!-- Cargando -->
    <div v-if="cargando" class="text-center py-5" style="color:var(--text-muted)">
      <div class="spinner-border spinner-border-sm me-2"></div>Cargando orden...
    </div>

    <!-- Error -->
    <div v-else-if="errorMensaje" class="alert alert-danger d-flex align-items-center gap-2 py-2">
      <i class="bi bi-exclamation-triangle-fill"></i>{{ errorMensaje }}
    </div>

    <template v-else-if="orden">

      <!-- Info de la orden -->
      <div class="card shadow-sm mb-3" style="background:var(--secondary-bg-main);border:1px solid var(--border-main)">
        <div class="card-header py-2 px-3 fw-semibold small"
             style="background:var(--primary-main);color:#fff;border-radius:.375rem .375rem 0 0">
          <i class="bi bi-info-circle me-1"></i>Informacion de la Orden
        </div>
        <div class="card-body py-3 px-3">
          <div class="row g-3 small">
            <div class="col-6 col-md-3">
              <div style="color:var(--text-secondary)">N° Orden</div>
              <div class="fw-semibold">{{ orden.numero_orden }}</div>
            </div>
            <div class="col-6 col-md-3">
              <div style="color:var(--text-secondary)">Estado</div>
              <span class="badge" :class="badgeEstado(orden.estado)">{{ labelEstado(orden.estado) }}</span>
            </div>
            <div class="col-6 col-md-3">
              <div style="color:var(--text-secondary)">Fecha ingreso</div>
              <div>{{ formatearFecha(orden.created_at) }}</div>
            </div>
            <div class="col-6 col-md-3">
              <div style="color:var(--text-secondary)">Cliente a facturar</div>
              <div class="fw-semibold">{{ orden.cliente_nombre || '— (sin definir)' }}</div>
            </div>

            <div class="col-6 col-md-3">
              <div style="color:var(--text-secondary)">Propietario</div>
              <div>{{ orden.propietario_nombre || '—' }}</div>
            </div>
            <div class="col-6 col-md-3">
              <div style="color:var(--text-secondary)">Telefono</div>
              <div>{{ orden.propietario_telefono || '—' }}</div>
            </div>
            <div class="col-6 col-md-3">
              <div style="color:var(--text-secondary)">Vehiculo</div>
              <div>{{ [orden.marca, orden.modelo].filter(Boolean).join(' ') || '—' }}</div>
            </div>
            <div class="col-6 col-md-3">
              <div style="color:var(--text-secondary)">Placa</div>
              <div>{{ orden.placa || '—' }}</div>
            </div>
            <div class="col-6 col-md-3">
              <div style="color:var(--text-secondary)">Color</div>
              <div>{{ orden.color || '—' }}</div>
            </div>
            <div class="col-6 col-md-3">
              <div style="color:var(--text-secondary)">Año</div>
              <div>{{ orden.anio || '—' }}</div>
            </div>
            <div class="col-6 col-md-3">
              <div style="color:var(--text-secondary)">Kilometraje</div>
              <div>{{ orden.kilometraje || '—' }}</div>
            </div>

            <div v-if="orden.observaciones" class="col-12">
              <div style="color:var(--text-secondary)">Observaciones</div>
              <div>{{ orden.observaciones }}</div>
            </div>
            <div v-if="orden.motivo_cancelacion" class="col-12">
              <div style="color:var(--text-secondary)">Motivo cancelación</div>
              <div class="text-danger">{{ orden.motivo_cancelacion }}</div>
            </div>
            <div v-if="orden.factura_id" class="col-12">
              <div style="color:var(--text-secondary)">Factura generada</div>
              <span class="badge bg-success"><i class="bi bi-receipt-cutoff me-1"></i>Factura #{{ orden.factura_id }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Detalle de repuestos -->
      <div class="card shadow-sm mb-3" style="background:var(--secondary-bg-main);border:1px solid var(--border-main)">
        <div class="card-header py-2 px-3 fw-semibold small"
             style="background:var(--primary-main);color:#fff;border-radius:.375rem .375rem 0 0">
          <i class="bi bi-box-seam me-1"></i>Repuestos utilizados
        </div>
        <div class="card-body p-0">
          <div class="table-responsive">
            <table class="table table-sm mb-0" style="font-size:.82rem">
              <thead style="background:var(--bg-main)">
                <tr style="color:var(--text-secondary)">
                  <th class="ps-3">Repuesto</th>
                  <th class="text-center">Cant.</th>
                  <th class="text-end">P. Unit.</th>
                  <th class="text-end">Desc.</th>
                  <th class="text-center">IVA %</th>
                  <th class="text-end pe-3">Total</th>
                </tr>
              </thead>
              <tbody style="color:var(--text-primary)">
                <tr v-for="d in orden.items" :key="d.id">
                  <td class="ps-3">
                    <div>{{ d.producto_nombre }}</div>
                    <div v-if="d.producto_codigo" class="text-muted" style="font-size:.75rem">{{ d.producto_codigo }}</div>
                  </td>
                  <td class="text-center">{{ d.cantidad }}</td>
                  <td class="text-end">$ {{ parseFloat(d.precio_unitario || 0).toFixed(2) }}</td>
                  <td class="text-end">$ {{ parseFloat(d.descuento || 0).toFixed(2) }}</td>
                  <td class="text-center">{{ parseFloat(d.porcentaje_iva || 0).toFixed(0) }}%</td>
                  <td class="text-end pe-3 fw-semibold" style="color:var(--accent-success)">
                    $ {{ parseFloat(d.total_detalle || 0).toFixed(2) }}
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr style="color:var(--text-secondary);font-size:.78rem">
                  <td colspan="5" class="text-end ps-3">Subtotal:</td>
                  <td class="text-end pe-3">$ {{ parseFloat(orden.subtotal || 0).toFixed(2) }}</td>
                </tr>
                <tr style="color:var(--text-secondary);font-size:.78rem">
                  <td colspan="5" class="text-end ps-3">IVA:</td>
                  <td class="text-end pe-3">$ {{ parseFloat(orden.iva || 0).toFixed(2) }}</td>
                </tr>
                <tr class="fw-bold border-top" style="color:var(--text-primary);font-size:.9rem">
                  <td colspan="5" class="text-end ps-3">TOTAL:</td>
                  <td class="text-end pe-3" style="color:var(--accent-success)">
                    $ {{ parseFloat(orden.total || 0).toFixed(2) }}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

    </template>

    <!-- ── Modal: Cambiar estado ── -->
    <div v-if="modalEstado.visible" class="modal-overlay" @click.self="modalEstado.visible = false">
      <div class="modal-caja" style="max-width:420px">
        <div class="modal-header-caja d-flex align-items-center justify-content-between">
          <span class="fw-semibold" style="color:var(--text-primary)">
            <i class="bi bi-arrow-left-right me-2" style="color:var(--primary-main)"></i>Cambiar Estado
          </span>
          <button class="btn-close-caja" @click="modalEstado.visible = false">✕</button>
        </div>
        <div class="modal-body-caja">
          <p class="small mb-2" style="color:var(--text-secondary)">
            Orden: <strong style="color:var(--text-primary)">{{ orden && orden.numero_orden }}</strong>
          </p>
          <div v-if="modalEstado.nuevoEstado === 'cancelada'" class="alert alert-warning py-2 small mb-3">
            <i class="bi bi-exclamation-triangle me-1"></i>
            Al cancelar, los repuestos ya cargados se devuelven al inventario.
          </div>
          <div class="mb-3">
            <label class="form-label small" style="color:var(--text-secondary)">Nuevo estado</label>
            <select
              v-model="modalEstado.nuevoEstado"
              class="form-select form-select-sm"
              style="background:var(--bg-main);color:var(--text-primary);border-color:var(--border-main)"
            >
              <option value="abierta">Abierta</option>
              <option value="en_proceso">En proceso</option>
              <option value="completada">Completada</option>
              <option value="cancelada">Cancelada</option>
            </select>
          </div>
          <div v-if="modalEstado.nuevoEstado === 'cancelada'" class="mb-3">
            <label class="form-label small" style="color:var(--text-secondary)">Motivo de cancelación</label>
            <input
              type="text"
              v-model="modalEstado.motivo"
              class="form-control form-control-sm"
              placeholder="Opcional"
              style="background:var(--bg-main);color:var(--text-primary);border-color:var(--border-main)"
            />
          </div>
        </div>
        <div class="modal-footer-caja d-flex gap-2 justify-content-end">
          <button class="btn btn-outline-secondary btn-sm" @click="modalEstado.visible = false">Cancelar</button>
          <button class="btn btn-primary btn-sm" @click="ejecutarCambioEstado" :disabled="modalEstado.guardando">
            <span v-if="modalEstado.guardando" class="spinner-border spinner-border-sm me-1"></span>
            Guardar
          </button>
        </div>
      </div>
    </div>

    <!-- ── Modal: Convertir a factura ── -->
    <div v-if="modalConversion.visible" class="modal-overlay" @click.self="modalConversion.visible = false">
      <div class="modal-caja" style="max-width:440px">
        <div class="modal-header-caja d-flex align-items-center justify-content-between">
          <span class="fw-semibold" style="color:var(--text-primary)">
            <i class="bi bi-receipt-cutoff me-2" style="color:var(--accent-success)"></i>Convertir a Factura
          </span>
          <button class="btn-close-caja" @click="modalConversion.visible = false">✕</button>
        </div>
        <div class="modal-body-caja">
          <div class="alert alert-info py-2 small">
            <i class="bi bi-info-circle me-1"></i>
            Se pre-cargarán los datos de la orden <strong>{{ orden && orden.numero_orden }}</strong>
            en el formulario de facturación.
          </div>
          <div v-if="modalConversion.cargando" class="text-center py-3" style="color:var(--text-muted)">
            <div class="spinner-border spinner-border-sm me-2"></div>Preparando datos...
          </div>
          <div v-if="modalConversion.error" class="alert alert-danger py-2 small">{{ modalConversion.error }}</div>
        </div>
        <div class="modal-footer-caja d-flex gap-2 justify-content-end">
          <button class="btn btn-outline-secondary btn-sm" @click="modalConversion.visible = false" :disabled="modalConversion.cargando">Cancelar</button>
          <button class="btn btn-success btn-sm" @click="ejecutarConversion" :disabled="modalConversion.cargando">
            <span v-if="modalConversion.cargando" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-arrow-right-circle me-1"></i>Ir a Facturación
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { ordenTallerService } from '@/services/ordenTallerService'
import notificaciones from '@/services/notificationService.js'

export default {
  name: 'ClientTallerOrdenVer',

  computed: {
    puedeEditar() {
      return this.$store.getters.puedoHacer('/taller/ordenes', 'editar')
    }
  },

  data() {
    return {
      cargando:     true,
      orden:        null,
      errorMensaje: '',

      modalEstado: {
        visible:     false,
        nuevoEstado: '',
        motivo:      '',
        guardando:   false
      },

      modalConversion: {
        visible:  false,
        cargando: false,
        error:    ''
      }
    }
  },

  mounted() {
    this.cargarOrden()
  },

  methods: {
    async cargarOrden() {
      this.cargando     = true
      this.errorMensaje = ''
      try {
        const res  = await ordenTallerService.obtener(this.$route.params.id)
        this.orden = res.data.data
      } catch {
        this.errorMensaje = 'No se pudo cargar la orden.'
      } finally {
        this.cargando = false
      }
    },

    abrirModalEstado() {
      this.modalEstado.nuevoEstado = this.orden.estado
      this.modalEstado.motivo      = ''
      this.modalEstado.guardando   = false
      this.modalEstado.visible     = true
    },

    async ejecutarCambioEstado() {
      this.modalEstado.guardando = true
      try {
        await ordenTallerService.cambiarEstado(
          this.orden.id,
          this.modalEstado.nuevoEstado,
          this.modalEstado.motivo || null
        )
        notificaciones.success('Estado actualizado a "' + this.labelEstado(this.modalEstado.nuevoEstado) + '".')
        this.modalEstado.visible = false
        await this.cargarOrden()
      } catch (e) {
        notificaciones.error(e?.response?.data?.error || 'Error al cambiar el estado.')
      } finally {
        this.modalEstado.guardando = false
      }
    },

    abrirModalConversion() {
      this.modalConversion.cargando = false
      this.modalConversion.error    = ''
      this.modalConversion.visible  = true
    },

    async ejecutarConversion() {
      this.modalConversion.cargando = true
      this.modalConversion.error    = ''
      try {
        const res   = await ordenTallerService.convertir(this.orden.id)
        const datos = res.data.data
        this.modalConversion.visible = false
        sessionStorage.setItem('orden_taller_prefill', JSON.stringify(datos))
        this.$router.push('/ventas/facturacion')
      } catch (e) {
        this.modalConversion.error = e?.response?.data?.error || 'Error al obtener los datos de la orden.'
      } finally {
        this.modalConversion.cargando = false
      }
    },

    formatearFecha(fecha) {
      if (!fecha) return '—'
      return new Date(fecha).toLocaleDateString('es-EC', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit'
      })
    },

    labelEstado(estado) {
      const mapa = {
        abierta:    'Abierta',
        en_proceso: 'En proceso',
        completada: 'Completada',
        cancelada:  'Cancelada',
        facturada:  'Facturada'
      }
      return mapa[estado] || estado
    },

    badgeEstado(estado) {
      const mapa = {
        abierta:    'bg-secondary',
        en_proceso: 'bg-warning text-dark',
        completada: 'bg-info text-dark',
        cancelada:  'bg-danger',
        facturada:  'bg-success'
      }
      return mapa[estado] || 'bg-secondary'
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.45);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.modal-caja {
  background: var(--secondary-bg-main);
  border: 1px solid var(--border-main);
  border-radius: .5rem;
  width: 100%;
  box-shadow: var(--shadow-md);
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  overflow: hidden;
}

.modal-header-caja {
  padding: .75rem 1rem;
  border-bottom: 1px solid var(--border-main);
  flex-shrink: 0;
}

.modal-body-caja {
  padding: 1rem;
  overflow-y: auto;
  flex: 1;
}

.modal-footer-caja {
  padding: .75rem 1rem;
  border-top: 1px solid var(--border-main);
  flex-shrink: 0;
}

.btn-close-caja {
  background: none;
  border: none;
  font-size: 1rem;
  cursor: pointer;
  color: var(--text-muted);
  line-height: 1;
  padding: 0;
}
.btn-close-caja:hover { color: var(--accent-danger); }
</style>
